import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useMessageSummary } from '../hooks/useMessageSummary';
import { useCompanyProfileAttention } from '../hooks/useCompanyProfileAttention';
import { adminApi, userPreferencesApi } from '../services/api';
import { FEATURE_DARK_MODE } from '../constants/featureFlags';
import { SUPPORT_CONTACT_PATH } from '../constants/support';
import { supportContextFromLocation, SupportContext } from '../utils/supportContext';
import { VersionIndicator } from './VersionIndicator';
import {
  AdminRuntimeSafetyOverlay,
  resetAdminRuntimeSafetyAcknowledgements
} from './AdminRuntimeSafetyOverlay';
import { AppShell } from './AppShell';
import { buildAppNavigation } from '../navigation/appNavigation';

type Theme = 'light' | 'dark';

interface DashboardLayoutProps {
  children: React.ReactNode;
  supportContext?: Pick<SupportContext, 'projectId' | 'contractId'>;
}

export function DashboardLayout({ children, supportContext }: DashboardLayoutProps) {
  const { t } = useTranslation();
  const { user, logout, endImpersonation } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const companyProfileAttention = useCompanyProfileAttention(
    !user?.isAdmin && !user?.isSupport && user?.personaType === 'company' ? user.id : undefined
  );
  // Only the parties of a project have milestone conversations (ClickUp 869fegbvk).
  const messageSummary = useMessageSummary(
    !!user && !user.isAdmin && !user.isSupport && ['person', 'company', 'builder'].includes(user.personaType)
  );
  const [theme, setTheme] = useState<Theme>(() => {
    // MVP: dark mode dold bakom FEATURE_DARK_MODE. Tvinga light utan att röra
    // det sparade värdet i localStorage (återanvänds när flaggan är på igen).
    if (!FEATURE_DARK_MODE) return 'light';
    const saved = localStorage.getItem('app-skin-dark');
    return saved === 'app-skin-dark' ? 'dark' : 'light';
  });
  const [debugConfigurable, setDebugConfigurable] = useState(true);
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
  const [isEndingImpersonation, setIsEndingImpersonation] = useState(false);

  const finishImpersonation = useCallback(async () => {
    if (isEndingImpersonation) return;
    setIsEndingImpersonation(true);
    try {
      await endImpersonation();
      navigate('/admin/profile', { replace: true });
    } finally {
      setIsEndingImpersonation(false);
    }
  }, [endImpersonation, isEndingImpersonation, navigate]);

  useEffect(() => {
    const impersonation = user?.impersonation;
    document.body.classList.toggle('impersonation-active', Boolean(impersonation));
    if (!impersonation) {
      setRemainingSeconds(null);
      return () => document.body.classList.remove('impersonation-active');
    }
    const updateRemaining = () => {
      const remaining = Math.max(
        0,
        Math.ceil((new Date(impersonation.expiresAt).getTime() - Date.now()) / 1000)
      );
      setRemainingSeconds(remaining);
    };
    updateRemaining();
    const timer = window.setInterval(updateRemaining, 1000);
    return () => {
      window.clearInterval(timer);
      document.body.classList.remove('impersonation-active');
    };
  }, [user?.impersonation]);

  useEffect(() => {
    if (user?.impersonation && remainingSeconds === 0) {
      void finishImpersonation();
    }
  }, [finishImpersonation, remainingSeconds, user?.impersonation]);

  useEffect(() => {
    if (!user?.impersonation) return;
    const root = document.querySelector<HTMLElement>('.main-content');
    if (!root) return;
    const disableWriteControls = () => {
      root
        .querySelectorAll<
          HTMLButtonElement | HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >(
          'form button[type="submit"], form input:not([type="search"]):not([type="hidden"]), form textarea, form select, [data-write-action="true"]'
        )
        .forEach((control) => {
          if (control.dataset.impersonationDisabled) return;
          control.dataset.impersonationDisabled = 'true';
          control.disabled = true;
          control.title = 'Skrivskyddat i personifierat läge';
        });
    };
    disableWriteControls();
    const observer = new MutationObserver(disableWriteControls);
    observer.observe(root, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      root
        .querySelectorAll<
          HTMLButtonElement | HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >('[data-impersonation-disabled="true"]')
        .forEach((control) => {
          control.disabled = false;
          control.removeAttribute('data-impersonation-disabled');
          control.removeAttribute('title');
        });
    };
  }, [user?.impersonation]);


  // Load theme preference from server on mount
  useEffect(() => {
    // MVP: när dark mode är avstängt ignoreras det sparade serverledet så att
    // en tidigare sparad "dark" inte tvingar fram mörkt läge. Värdet raderas
    // inte – det ligger kvar på servern och återanvänds vid återaktivering.
    if (!FEATURE_DARK_MODE) return;
    const loadThemeFromServer = async () => {
      try {
        const prefs = await userPreferencesApi.getPreferences();
        if (prefs.theme) {
          setTheme(prefs.theme as Theme);
        }
      } catch (error) {
        console.error('Failed to load theme preference:', error);
      }
    };
    loadThemeFromServer();
  }, []);

  useEffect(() => {
    if (!user?.isAdmin) return;

    adminApi.getDebugSettings()
      .then(settings => setDebugConfigurable(settings.debugConfigurable))
      .catch(() => setDebugConfigurable(false));
  }, [user?.isAdmin]);

  // Apply theme to DOM when changed
  useEffect(() => {
    // MVP: tvinga alltid light mode när flaggan är av. Ta bara bort klassen –
    // rör inte localStorage så det sparade temavärdet bevaras för framtiden.
    if (!FEATURE_DARK_MODE) {
      document.documentElement.classList.remove('app-skin-dark');
      return;
    }
    if (theme === 'dark') {
      document.documentElement.classList.add('app-skin-dark');
      localStorage.setItem('app-skin-dark', 'app-skin-dark');
    } else {
      document.documentElement.classList.remove('app-skin-dark');
      localStorage.setItem('app-skin-dark', 'app-skin-light');
    }
  }, [theme]);

  const handleLogout = () => {
    resetAdminRuntimeSafetyAcknowledgements();
    logout();
    navigate('/login');
  };

  const pageContent = (
    <>
          {user?.impersonation && (
            <div className="impersonation-banner" role="status" aria-live="polite">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <i className="feather-eye" aria-hidden="true" />
                <div>
                  <strong>Personifierat läsläge</strong>
                  <span className="ms-2">
                    {user.impersonation.targetName} ({user.impersonation.targetEmail})
                  </span>
                </div>
                <span className="impersonation-countdown ms-md-auto">
                  {String(Math.floor((remainingSeconds ?? 0) / 60)).padStart(2, '0')}:
                  {String((remainingSeconds ?? 0) % 60).padStart(2, '0')} kvar
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={() => void finishImpersonation()}
                  disabled={isEndingImpersonation}
                >
                  {isEndingImpersonation ? 'Avslutar...' : 'Avsluta personifiering'}
                </button>
              </div>
            </div>
          )}
          <div className="main-content">{children}</div>
          <footer className="px-4 pb-4 text-center small">
            <nav
              aria-label={t('landing.footer.navigationLabel')}
              className="d-flex flex-wrap gap-3 justify-content-center"
            >
              <Link to="/anvandarvillkor" className="text-muted text-nowrap">
                {t('landing.footer.termsOfService')}
              </Link>
              <Link to="/integritet" className="text-muted text-nowrap">
                {t('landing.footer.privacyPolicy')}
              </Link>
              <Link to="/aterbetalning-och-avbokning" className="text-muted text-nowrap">
                {t('landing.footer.refundPolicy')}
              </Link>
              <Link to="/leveransinformation" className="text-muted text-nowrap">
                {t('landing.footer.deliveryInformation')}
              </Link>
              <Link to={SUPPORT_CONTACT_PATH} state={{ supportContext: supportContextFromLocation(location, supportContext) }} className="text-muted text-nowrap">
                {t('landing.footer.contact')}
              </Link>
            </nav>
            <div className="mt-2">
              <VersionIndicator isAdmin={Boolean(user?.isAdmin)} />
            </div>
          </footer>
    </>
  );

  const appNavigation = buildAppNavigation(user, t, {
    companyProfileAttention,
    debugConfigurable,
    unreadMessages: messageSummary?.unread,
  });
  // The environment bar is mounted once in App, above every page.
  const indicators = <AdminRuntimeSafetyOverlay enabled={Boolean(user?.isAdmin)} />;
  // Before anyone is signed in there is no menu, only the page.
  if (!appNavigation) {
    return (
      <>
        {indicators}
        <div className="nxl-content">{pageContent}</div>
      </>
    );
  }
  return (
    <AppShell
      navigation={appNavigation}
      userName={user?.fullName ?? ''}
      userEmail={user?.email ?? ''}
      onLogout={handleLogout}
      before={indicators}
    >
      <div className="nxl-content">{pageContent}</div>
    </AppShell>
  );
}
